import React, { useState } from "react";
import {
  Card,
  Typography,
  Box,
  Stack,
  useTheme,
  useMediaQuery,
  Button,
  Chip,
} from "@mui/material";
import {
  StarRounded as StarIcon,
  LocationOn as LocationIcon,
  FitnessCenter as FitnessIcon,
  ArrowForwardRounded as ArrowForwardIcon,
} from "@mui/icons-material";
import { useNavigate } from "react-router-dom";

export default function CoachCard({ trainer }) {
  const theme = useTheme();
  const navigate = useNavigate();
  const isMobile = useMediaQuery(theme.breakpoints.down("sm"));
  const [hovered, setHovered] = useState(false);

  // استخراج اطلاعات ضروری
  const getCoachName = () => trainer?.fullname || "مربی بدون نام";
  const getRating = () => trainer?.rating_stats?.average_rating || 0;

  // فقط رشته اصلی
  const getMainSpecialty = () => trainer?.mainSpecialty?.name || "بدنسازی";

  // گرفتن تمامی مناطق و چسباندن آن‌ها با کاما
  const getAllLocations = () => {
    if (trainer?.areas?.length) {
      return trainer.areas.map((a) => (
        <Typography key={a?.name} sx={{ fontSize: ".7rem" }}>
          {a.name}
        </Typography>
      ));
    }
    return "همه مناطق";
  };

  // گرفتن بهترین عکس برای پس‌زمینه
  const getCoverSrc = () => {
    const img = trainer?.coverImage || trainer?.images?.[0];
    return img ? `${import.meta.env.VITE_URL}${img}` : null;
  };

  return (
    <Card
      onClick={() =>
        navigate(
          `/coaches/${trainer?._id}/${trainer?.fullname?.replaceAll(" ", "-")}`,
        )
      }
      onMouseEnter={() => !isMobile && setHovered(true)}
      onMouseLeave={() => !isMobile && setHovered(false)}
      sx={{
        height: { xs: 400, sm: 460 },
        position: "relative",
        borderRadius: 4,
        overflow: "hidden",
        cursor: "pointer",
        boxShadow: hovered
          ? "0px 20px 40px rgba(0, 0, 0, 0.3)"
          : "0px 8px 24px rgba(0, 0, 0, 0.12)",
        transition: "all 0.4s cubic-bezier(0.175, 0.885, 0.32, 1.275)",
        transform: hovered && !isMobile ? "translateY(-8px)" : "none",
        bgcolor: theme.palette.grey[900],
      }}
    >
      {trainer?.isDiscount && (
        <Chip
          label={"دارای تخفیف"}
          size="small"
          sx={{
            position: "absolute",
            top: 12,
            right: 12,
            background: "linear-gradient(45deg, #FF1744 30%, #FF5252 90%)",
            color: "#fff",
            fontWeight: 700,
            fontSize: "0.7rem",
            boxShadow: "0px 4px 12px rgba(255, 23, 68, 0.4)",
            zIndex: "10000",
          }}
        />
      )}
      {/* ================= BACKGROUND IMAGE ================= */}
      <Box
        sx={{
          position: "absolute",
          inset: 0,
          backgroundImage: getCoverSrc() ? `url(${getCoverSrc()})` : "none",
          backgroundSize: "cover",
          backgroundPosition: "center top",
          transition: "transform 0.7s ease",
          transform: hovered ? "scale(1.05)" : "scale(1)",
        }}
      />

      {/* ================= DARK GRADIENT OVERLAY ================= */}
      <Box
        sx={{
          position: "absolute",
          inset: 0,
          background:
            "linear-gradient(to top, rgba(0,0,0,0.95) 0%, rgba(0,0,0,0.7) 25%, rgba(0,0,0,0) 55%)",
          pointerEvents: "none",
          transition: "opacity 0.4s ease",
          opacity: 0.9,
        }}
      />

      {/* ================= BOTTOM CONTENT ================= */}
      <Box
        sx={{
          position: "absolute",
          bottom: 0,
          left: 0,
          right: 0,
          p: 2.5,
          display: "flex",
          flexDirection: "column",
          gap: 1.5,
          zIndex: 3,
        }}
      >
        {/* Name and Rating */}
        <Stack
          direction="row"
          justifyContent="space-between"
          alignItems="flex-end"
        >
          <Typography
            variant="h6"
            sx={{
              color: "#fff",
              fontWeight: 800,
              textShadow: "0 2px 8px rgba(0,0,0,0.5)",
              lineHeight: 1.2,
            }}
          >
            {getCoachName()}
          </Typography>

          <Stack direction="row" alignItems="center" gap={0.5} sx={{ mb: 0.5 }}>
            <Typography
              variant="subtitle2"
              sx={{ color: "#fff", fontWeight: 700 }}
            >
              {getRating().toLocaleString("fa-IR")}
            </Typography>
            <StarIcon sx={{ fontSize: 20, color: "#faaf00" }} />
          </Stack>
        </Stack>

        {/* Specialty and All Locations (One Line) */}
        <Stack
          direction="column"
          gap={1.5}
          // alignItems="center"
          sx={{ width: "100%", overflow: "hidden" }}
        >
          {/* Specialty */}
          <Stack
            direction="row"
            alignItems="center"
            gap={0.5}
            sx={{ flexShrink: 0 }}
          >
            <FitnessIcon
              sx={{ fontSize: 16, color: "rgba(255,255,255,0.7)" }}
            />
            <Typography
              variant="body2"
              sx={{ color: "rgba(255,255,255,0.9)", fontWeight: 500 }}
            >
              {getMainSpecialty()}
            </Typography>
          </Stack>

          {/* Locations (Truncates with ... if too long) */}
          <Stack
            direction="row"
            alignItems="center"
            gap={0.5}
            sx={{ flex: 1, minWidth: 0 }}
          >
            <LocationIcon
              sx={{
                fontSize: 16,
                color: "rgba(255,255,255,0.7)",
                flexShrink: 0,
              }}
            />
            <Stack
              direction={"row"}
              // variant="body2"
              // noWrap
              sx={{
                color: "rgba(255,255,255,0.9)",
                fontWeight: 500,
                width: "100%",
                gap: "4px",
                // display: "block",
              }}
            >
              {getAllLocations()}
            </Stack>
          </Stack>
        </Stack>

        {/* Action Button */}
        <Button
          variant="contained"
          fullWidth
          disableElevation
          endIcon={
            <ArrowForwardIcon
              sx={{ mr: 1, ml: -1, fontSize: 18, rotate: "180deg" }}
            />
          }
          sx={{
            mt: 1,
            py: 1,
            bgcolor: hovered
              ? theme.palette.primary.main
              : "rgba(255,255,255,0.15)",
            backdropFilter: "blur(10px)",
            color: "#fff",
            borderRadius: 2.5,
            fontWeight: 700,
            transition: "all 0.3s ease",
            "&:hover": {
              bgcolor: theme.palette.primary.main,
            },
          }}
        >
          مشاهده مربی
        </Button>
      </Box>
    </Card>
  );
}
